import React, { useEffect, useRef, useState } from 'react';
import { useDisclosure, useToast, Progress, Box, HStack, RadioGroup, Radio } from '@chakra-ui/react';
import { FaRegTrashAlt } from 'react-icons/fa';
import { BiPlus } from 'react-icons/bi';
import ImagePreviewModal from '../Modals/ImagePreviewModal';

const SingleImageField: React.FC<any> = ({ field, value, onFileChange, invalid }) => {
  const [image, setImage] = useState<string | null>(null);
  const [uploadingPreview, setUploadingPreview] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const initialMode: 'na' | 'val' = value === 'N/A' ? 'na' : 'val';
  const [mode, setMode] = useState<'na' | 'val'>(initialMode);

  useEffect(() => {
    if (value && value !== 'N/A') {
      setImage(value);
      setMode('val');
    } else if (value === 'N/A') {
      setImage(null);
      setMode('na');
    } else {
      setImage(null);
      setMode('val');
    }
  }, [value]);

  const handleFileChange = (file: File) => {
    if (mode === 'na') return;
    if (!file || !file.type.startsWith('image/')) {
      toast({
        title: 'Not a valid image',
        description: 'Please upload a valid image file',
        status: 'warning',
        position: 'top',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const reader = new FileReader();
    setUploadingPreview(URL.createObjectURL(file));
    setUploadProgress(0);

    reader.onprogress = (e) => {
      if (e.loaded && e.total) {
        const progress = Math.round((e.loaded / e.total) * 100);
        setUploadProgress(progress);
      }
    };

    reader.onloadend = () => {
      const result = reader.result as string;
      setImage(result);
      onFileChange?.(result);
      setUploadingPreview(null);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    };

    reader.readAsDataURL(file);
  };

  const handleRemove = () => {
    if (mode === 'na') return;
    setImage(null);
    onFileChange?.(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (mode === 'na') return;
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileChange(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (mode !== 'na') setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);
  const handleAreaClick = () => mode !== 'na' && fileInputRef.current?.click();

  return (
    <div data-field-name={field?.name}>
      <b>{field.label}</b>
      {!field.nullable && <span style={{ color: 'red' }}>*</span>}

      {field.nullable === true && (
        <RadioGroup value={mode} onChange={n => setMode(n as 'na' | 'val')}>
          <HStack mb={2}>
            <Radio value="na">TBD</Radio>
          </HStack>
          <HStack align="start" spacing={2}>
            <Radio value="val" aria-label="Provide values" mt="6px" />
            <div className="single-image-field-container" style={{ flex: 1, opacity: mode === 'na' ? 0.6 : 1, pointerEvents: mode === 'na' ? 'none' : 'auto' }}>
              {image && (
                <div className="img-preview">
                  <div style={{ position: 'relative' }}>
                    <img
                      src={image}
                      alt="Uploaded"
                      onClick={onOpen}
                      style={{ cursor: 'zoom-in' }}
                    />
                  </div>
                  <button
                    type="button"
                    className="remove-img-btn"
                    onClick={handleRemove}
                    aria-label="Remove image"
                    title="Remove image"
                  >
                    <FaRegTrashAlt />
                  </button>
                </div>
              )}

              {!image && uploadingPreview && (
                <Box className={!!invalid ? 'single-img-upload-area invalid' : 'single-img-upload-area'} style={{ position: 'relative' }}>
                  <img
                    src={uploadingPreview}
                    alt="Uploading preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, opacity: 0.6 }}
                  />
                  <Progress value={uploadProgress} size="xs" position="absolute" bottom="0" width="100%" />
                </Box>
              )}

              {!image && !uploadingPreview && (
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={handleAreaClick}
                  className={!!invalid ? 'single-img-upload-area invalid' : 'single-img-upload-area'}
                  style={{ border: isDragging ? '1px dashed #4CAF50' : undefined }}
                >
                  <BiPlus />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileChange(file);
                    }}
                    style={{ display: 'none' }}
                  />
                </div>
              )}
            </div>
          </HStack>
        </RadioGroup>
      )}

      {field.nullable !== true && (
        <div className="single-image-field-container">
          {image && (
            <div className="img-preview">
              <div style={{ position: 'relative' }}>
                <img
                  src={image}
                  alt="Uploaded"
                  onClick={onOpen}
                  style={{ cursor: 'zoom-in' }}
                />
              </div>
              <button
                type="button"
                className="remove-img-btn"
                onClick={handleRemove}
                aria-label="Remove image"
                title="Remove image"
              >
                <FaRegTrashAlt />
              </button>
            </div>
          )}

          {!image && uploadingPreview && (
            <Box className={!!invalid ? 'single-img-upload-area invalid' : 'single-img-upload-area'} style={{ position: 'relative' }}>
              <img
                src={uploadingPreview}
                alt="Uploading preview"
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, opacity: 0.6 }}
              />
              <Progress value={uploadProgress} size="xs" position="absolute" bottom="0" width="100%" />
            </Box>
          )}

          {!image && !uploadingPreview && (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={handleAreaClick}
              className={!!invalid ? 'single-img-upload-area invalid' : 'single-img-upload-area'}
              style={{ border: isDragging ? '1px dashed #4CAF50' : undefined }}
            >
              <BiPlus />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileChange(file);
                }}
                style={{ display: 'none' }}
              />
            </div>
          )}
        </div>
      )}

      {mode === 'val' && <input type="hidden" name={field.name} value={image || ''} />}
      {mode === 'na' && <input type="hidden" name={field.name} value="N/A" />}

      {isOpen && image && <ImagePreviewModal isOpen={isOpen} onClose={onClose} image={image} />}
    </div>
  );
};

export default SingleImageField;
